import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useImmer } from 'use-immer';

import { AdminSection } from '@typings';
import { getSystemUsers } from '@services';
import { userSelector } from '@selectors';
import { useFetchList, useSelector } from '@hooks';

import { ChaseSpinner, Field, Render, Table } from '@components';
import {
  AdminSectionUserActions,
  SetRoleUserModal,
} from '@components/AdminSection';

const ITEMS_PER_PAGE = 50;

const ROLE_OPTIONS = [
  { value: 'all', text: 'All' },
  { value: AdminSection.Role.Admin, text: 'Admin' },
  { value: AdminSection.Role.Manager, text: 'Manager' },
  { value: AdminSection.Role.User, text: 'User' },
];

export const AdminSectionUsersPage = () => {
  const { username, systemRole } = useSelector(userSelector);

  const [filters, setFilters] = useImmer({
    role: 'all',
    search: '',
  });

  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  const loaderRef = useRef<HTMLDivElement | null>(null);

  const { list, isFetched, setList } = useFetchList<AdminSection.User[]>({
    getList: getSystemUsers,
  });

  const handleFilterChange = (key: keyof typeof filters, value: string) => {
    setFilters((prev) => {
      prev[key] = value;
    });
  };

  const handleRemoveUserRole = useCallback(
    (name: AdminSection.User['name']) => {
      setList((prev) => prev?.filter((user) => user.name !== name) ?? null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleUpdateUserRole = useCallback(
    (name: AdminSection.User['name'], role?: AdminSection.Role) => {
      setList(
        (prev) =>
          prev?.map((user) => {
            if (user.name === name) {
              return { ...user, role };
            }

            return user;
          }) ?? null,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const isUserActionsVisible = useCallback(
    (user: AdminSection.User) => {
      if (username === user.name) {
        return false;
      }

      switch (systemRole) {
        case AdminSection.Role.User:
          // The system user role can only read all system users
          return false;

        case AdminSection.Role.Manager:
          // The manager can only update the user role of other managers
          return user.role !== AdminSection.Role.Admin;

        case AdminSection.Role.Admin:
          // The admin can update the user role of all users
          return true;

        default:
          return false;
      }
    },
    [systemRole, username],
  );

  const handleInviteUsers = useCallback(
    (user: AdminSection.User) => {
      setList((prev) => (prev ? [...prev, user] : [user]));
    },
    [setList],
  );

  const filteredUsers = useMemo(() => {
    if (filters.search.length === 0 && filters.role === 'all') {
      return list;
    }

    const searchLower = filters.search.toLowerCase();

    return list.filter(
      ({ name, email, firstName, lastName, role }) =>
        (filters.role === 'all' || role === filters.role) &&
        [name, email, firstName, lastName].some(
          (field) => field?.toLowerCase().includes(searchLower),
        ),
    );
  }, [filters.role, filters.search, list]);

  const usersToShow = useMemo(() => {
    return filteredUsers.slice(0, visibleCount);
  }, [filteredUsers, visibleCount]);

  useEffect(() => {
    if (!loaderRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setVisibleCount((prev) =>
            Math.min(prev + ITEMS_PER_PAGE, filteredUsers.length),
          );
        }
      },
      {
        rootMargin: '100px',
      },
    );

    observer.observe(loaderRef.current);

    return () => observer.disconnect();
  }, [filteredUsers.length]);

  useEffect(() => {
    // Reset pagination when filters changes
    setVisibleCount(ITEMS_PER_PAGE);
  }, [filters]);

  if (!isFetched) {
    return (
      <div className="relative flex min-h-[400px] w-full items-center justify-center">
        <ChaseSpinner color="black" className="h-12 w-12" />
      </div>
    );
  }

  return (
    <>
      <div className="mt-6 flex items-center gap-10">
        <Render if={systemRole !== AdminSection.Role.User}>
          <SetRoleUserModal excludedUsers={list} onInvite={handleInviteUsers} />
        </Render>

        <Field.Input
          containerClassName="flex-1"
          className="border-neural-03"
          label="Search"
          value={filters.search}
          onChange={(event) => handleFilterChange('search', event.target.value)}
        />

        <Field.Select
          label="Role"
          className="w-[140px] border-neural-03"
          value={filters.role}
          options={ROLE_OPTIONS}
          onChange={(event) => handleFilterChange('role', event.target.value)}
        />
      </div>

      <Table className="mt-6 w-full auto-rows-min grid-cols-[repeat(4,minmax(max-content,auto))_min-content] overflow-auto">
        <Table.Header>
          <Table.Row>
            <Table.Head>Name</Table.Head>
            <Table.Head>Role</Table.Head>
            <Table.Head>Email</Table.Head>
            <Table.Head>Sign up date</Table.Head>
            <Table.Head />
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {usersToShow.map((user) => (
            <Table.Row key={user.name} className="hover-td group">
              <Table.Cell>{user.name}</Table.Cell>
              <Table.Cell className="capitalize">
                {user.role ?? 'no role'}
              </Table.Cell>
              <Table.Cell>{user.email}</Table.Cell>
              <Table.Cell>
                {user.createdAt && dayjs(user.createdAt).format('L')}
              </Table.Cell>
              <Table.Cell className="flex flex-row place-items-center gap-3">
                <Render if={isUserActionsVisible(user)}>
                  <AdminSectionUserActions
                    user={user}
                    onRemoveRole={handleRemoveUserRole}
                    onUpdateRole={handleUpdateUserRole}
                  />
                </Render>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <div ref={loaderRef} className="h-10" />

      <Render if={filteredUsers.length > visibleCount}>
        <div className="flex w-full items-center justify-center">
          <ChaseSpinner color="black" className="h-8 w-8" />
        </div>
      </Render>

      <Render if={filteredUsers.length === 0}>
        <p className="break-words text-center text-neural-04">No users found</p>
      </Render>
    </>
  );
};
